(() => {
  'use strict';

  const data = window.CUSTOMIZER_DATA || {};
  const components = data.components || {};
  const product = data.product || {};
  const defaults = data.defaults || {};
  const STORAGE_KEY = 'tolunWatchCustomizerDesignV10';
  const TYPES = ['dial', 'case', 'strap', 'hands'];
  const ENGRAVING_ENABLED = false;
  const CURRENT_CUSTOMIZER_SCRIPT_SRC = document.currentScript?.src || '';
  // Shopify'daki eski İbre varyant adlarını ziyaretçiye Kol olarak göster.
  const displayPartName = name => String(name ?? '').replace(/\bİbre\b/g, 'Kol').replace(/\bibre\b/g, 'kol');

  const COMPONENT_PRODUCT_HANDLES = {
    dial: 'tolun-kadran-parcalari',
    case: 'tolun-kasa-parcalari',
    strap: 'tolun-kordon-parcalari',
    hands: 'tolun-ibre-parcalari'
  };

  async function fetchComponentProduct(handle, forceFresh = false) {
    const root = window.Shopify?.routes?.root || '/';
    const cleanRoot = root.endsWith('/') ? root : `${root}/`;
    const url = `${cleanRoot}products/${encodeURIComponent(handle)}?section_id=tolun-component-data${forceFresh ? `&_tolun_stock=${Date.now()}` : ''}`;

    const response = await fetch(url, {
      method: 'GET',
      credentials: 'same-origin',
      cache: forceFresh ? 'no-store' : 'default',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });

    if (!response.ok) {
      throw new Error(`Component request failed: ${handle} (${response.status})`);
    }

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const jsonNode = doc.querySelector('[data-tolun-component-json]');

    if (!jsonNode) {
      throw new Error(`Component JSON not found: ${handle}`);
    }

    const payload = JSON.parse(jsonNode.textContent || '{}');
    return Array.isArray(payload.variants) ? payload.variants : [];
  }

  // Shopify ürün verisi otoritedir: silinen varyantlar statik listede kalmaz.
  // Bir ürünün verisi alınamazsa eski listeyi göstermek yerine o tipin
  // seçeneklerini boş tutuyoruz; yanlışlıkla silinmiş parça seçilemez.
  async function loadShopifyComponents() {
    const results = await Promise.all(TYPES.map(async type => {
      try {
        const variants = await fetchComponentProduct(COMPONENT_PRODUCT_HANDLES[type], true);
        return [type, { ok: true, variants }];
      } catch (error) {
        console.warn(`[TOLUN] ${type} Shopify verisi alınamadı; eski seçenekler gösterilmeyecek.`, error);
        return [type, { ok: false, variants: [] }];
      }
    }));
    return Object.fromEntries(results);
  }

  function hydrateComponentsFromShopify(shopifyComponents) {
    const report = {};

    TYPES.forEach(type => {
      const result = shopifyComponents[type];
      const localItems = components[type] || [];
      const originalCount = localItems.length;

      if (!result?.ok) {
        components[type] = [];
        report[type] = { status: 'unavailable', received: 0, removed: originalCount };
        return;
      }

      const liveItems = result.variants || [];

      const localByVariantId = new Map(
        localItems
          .filter(item => Number.isSafeInteger(Number(item?.shopifyVariantId)) && Number(item.shopifyVariantId) > 0)
          .map(item => [Number(item.shopifyVariantId), item])
      );

      const localBySku = new Map(
        localItems
          .filter(item => item?.sku)
          .map(item => [String(item.sku).trim().toUpperCase(), item])
      );

      const nextItems = [];
      const seenVariantIds = new Set();
      let matchedByVariantId = 0;
      let matchedBySku = 0;
      let added = 0;
      let skipped = 0;

      liveItems.forEach(live => {
        const variantId = Number(live?.id);
        if (!Number.isSafeInteger(variantId) || variantId <= 0) {
          skipped++;
          return;
        }

        if (seenVariantIds.has(variantId)) {
          skipped++;
          return;
        }
        seenVariantIds.add(variantId);

        const liveSku = String(live.sku || '').trim();
        const skuKey = liveSku.toUpperCase();

        let item = localByVariantId.get(variantId);

        if (item) {
          matchedByVariantId++;
        } else if (skuKey && localBySku.has(skuKey)) {
          // Eski tema verilerinde Variant ID kayıtlı değilse ilk geçişte SKU ile bağla.
          item = localBySku.get(skuKey);
          matchedBySku++;
        } else {
          if (!live.image || (type === 'dial' && !live.collection)) {
            console.warn(`[TOLUN] Yeni ${type} varyantı eksik görsel/koleksiyon nedeniyle atlandı: ${liveSku || variantId}`);
            skipped++;
            return;
          }

          item = {
            id: `shopify-${type}-${variantId}`,
            sku: liveSku,
            name: displayPartName(live.title || liveSku || String(variantId)),
            image: live.image,
            thumbnail: live.image,
            price: 0,
            stock: null,
            shopifyVariantId: variantId
          };
          added++;
        }

        // Shopify paneli artık isim ve SKU için de tek kaynaktır.
        // Panelde değiştirilen değerler her sayfa açılışında customizer'a yansır.
        item.shopifyVariantId = variantId;
        item.sku = liveSku;
        item.name = displayPartName(live.title || liveSku || item.name || String(variantId));
        item.stock = live.stock == null || live.stock === ''
          ? null
          : (Number.isFinite(Number(live.stock)) ? Number(live.stock) : null);
        item.available = live.available !== false;
        item.price = Number(live.price || 0);

        if (live.image) {
          item.image = live.image;
          item.thumbnail = live.image;
        }

        if (type === 'dial') {
          item.collection = live.collection || item.collection || '';
          item.collectionTitle = live.collectionTitle || item.collectionTitle || '';
        }

        nextItems.push(item);
      });

      components[type] = nextItems;
      report[type] = {
        status: 'ok',
        received: liveItems.length,
        matchedByVariantId,
        matchedBySku,
        added,
        skipped,
        removed: Math.max(0, originalCount - matchedByVariantId - matchedBySku)
      };
    });

    window.TOLUN_COMPONENT_SYNC = report;
    return report;
  }

  const urlParams = new URLSearchParams(window.location.search);
  const collectionTitleMap = window.TOLUN_COLLECTION_TITLES || {};
  const requestedCollection = normalizeCollectionHandle(urlParams.get('collection') || '');
  let activeDialCollection = '';

  function normalizeCollectionHandle(value) {
    return String(value || '')
      .trim()
      .toLocaleLowerCase('tr-TR')
      .replace(/ı/g, 'i')
      .replace(/ğ/g, 'g')
      .replace(/ü/g, 'u')
      .replace(/ş/g, 's')
      .replace(/ö/g, 'o')
      .replace(/ç/g, 'c')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function collectionTitle(handle) {
    if (!handle) return '';
    if (collectionTitleMap[handle]) return collectionTitleMap[handle];

    return handle
      .split('-')
      .filter(Boolean)
      .map(word => word.charAt(0).toLocaleUpperCase('tr-TR') + word.slice(1))
      .join(' ');
  }

  function dialCollections() {
    const map = new Map();

    (components.dial || []).forEach(item => {
      const handle = normalizeCollectionHandle(item.collection || '');
      if (!handle || map.has(handle)) return;
      map.set(handle, {
        handle,
        title: item.collectionTitle || collectionTitle(handle)
      });
    });

    return [...map.values()];
  }

  function visibleItems(type) {
    const items = components[type] || [];
    if (type !== 'dial' || !activeDialCollection) return items;
    return items.filter(item =>
      normalizeCollectionHandle(item.collection || '') === activeDialCollection
    );
  }

  const state = {
    view: 'front',
    quantity: 1,
    engraving: '',
    selected: {
      dial: defaults.dial,
      case: defaults.case,
      strap: defaults.strap,
      hands: defaults.hands
    }
  };

  const els = {
    root: document.querySelector('[data-tolun-customizer]'),
    previewPanel: document.querySelector('[data-tolun-customizer] .preview-panel'),
    watchStage: document.querySelector('[data-tolun-customizer] .watch-stage'),
    optionsPanel: document.querySelector('[data-tolun-customizer] .options-panel'),
    canvas: document.getElementById('watchCanvas'),
    thumbCanvas: document.getElementById('frontThumbCanvas'),
    loader: document.getElementById('renderLoader'),
    chips: document.getElementById('selectionChips'),
    totalPrice: document.getElementById('totalPrice'),
    priceNote: document.getElementById('priceNote'),
    engravingInput: document.getElementById('engravingInput'),
    engravingCount: document.getElementById('engravingCount'),
    qtyValue: document.getElementById('qtyValue'),
    toast: document.getElementById('toast'),
    addToCart: document.getElementById('addToCartBtn'),
    dialCollectionTabs: document.getElementById('dialCollectionTabs')
  };

  const ctx = els.canvas.getContext('2d', { alpha: true, desynchronized: true });
  const thumbCtx = els.thumbCanvas.getContext('2d', { alpha: true, desynchronized: true });

  // Mobilde ana önizleme ekrandan çıktığında görünen küçük canlı önizleme.
  const livePreview = document.createElement('aside');
  livePreview.className = 'mobile-live-preview';
  livePreview.setAttribute('aria-label', 'Canlı saat önizlemesi');
  livePreview.innerHTML = `
    <span class="mobile-live-preview__label"><i></i>LIVE PREVIEW</span>
    <canvas id="mobileLivePreviewCanvas" width="1100" height="1100" aria-hidden="true"></canvas>
  `;
  els.root?.appendChild(livePreview);

  els.livePreview = livePreview;
  els.liveCanvas = livePreview.querySelector('#mobileLivePreviewCanvas');
  const liveCtx = els.liveCanvas.getContext('2d', { alpha: true, desynchronized: true });
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  thumbCtx.imageSmoothingEnabled = true;
  thumbCtx.imageSmoothingQuality = 'high';
  liveCtx.imageSmoothingEnabled = true;
  liveCtx.imageSmoothingQuality = 'high';

  const imageCache = new Map();
  let renderSequence = 0;

  const byId = (type, id) => (components[type] || []).find(item => item.id === id);
  const selectedItem = type => byId(type, state.selected[type]);
  const isAvailable = item => !!item &&
    item.available !== false &&
    (item.stock === null || item.stock === undefined || Number(item.stock) > 0);

  function formatTRY(value) {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency', currency: 'TRY', maximumFractionDigits: 0
    }).format(value || 0);
  }

  function total() {
    let value = Number(product.basePrice || 0);
    TYPES.forEach(type => value += Number(selectedItem(type)?.price || 0));
    if (ENGRAVING_ENABLED && state.engraving) value += Number(product.engravingPrice || 0);
    return value * state.quantity;
  }

  function renderPrice() {
    els.totalPrice.textContent = formatTRY(total());
    els.priceNote.textContent = product.basePriceIsDemo ? 'Tahmini fiyat — gerçek sepet toplamı Shopify fiyatlarına bağlıdır' : 'KDV dahil — sepet toplamını kontrol edin';
  }

  function loadImage(src) {
    if (!src) return Promise.reject(new Error('Görsel yolu boş'));
    if (imageCache.has(src)) return imageCache.get(src);

    const promise = new Promise((resolve, reject) => {
      const img = new Image();
      img.decoding = 'async';
      img.onload = async () => {
        try { if (img.decode) await img.decode(); } catch (_) {}
        resolve(img);
      };
      img.onerror = () => reject(new Error(`Görsel yüklenemedi: ${src}`));
      img.src = src;
    });

    imageCache.set(src, promise);
    return promise;
  }

  function clearCanvas(targetCtx) {
    targetCtx.clearRect(0, 0, 1100, 1100);
  }

  function drawFrontTo(targetCtx, images) {
    clearCanvas(targetCtx);
    images.forEach(img => targetCtx.drawImage(img, 0, 0, 1100, 1100));
  }

  async function renderFront(forceLoader = false) {
    const seq = ++renderSequence;
    const items = TYPES.map(selectedItem);
    const sources = items.map(item => item?.image).filter(Boolean);
    if (sources.length !== 4) return;

    if (forceLoader) els.loader.classList.add('is-visible');

    try {
      const images = await Promise.all(sources.map(loadImage));
      if (seq !== renderSequence || state.view !== 'front') return;
      drawFrontTo(ctx, images);
      drawFrontTo(thumbCtx, images);
      drawFrontTo(liveCtx, images);
      els.canvas.classList.add('is-ready');
      els.liveCanvas.classList.add('is-ready');
    } catch (err) {
      console.error(err);
      toast('Bir parça görseli yüklenemedi.');
    } finally {
      if (seq === renderSequence) els.loader.classList.remove('is-visible');
    }
  }

  async function renderStaticView(viewName) {
    const seq = ++renderSequence;
    const view = data.views?.[viewName];
    if (!view?.image) return;
    els.loader.classList.add('is-visible');
    try {
      const img = await loadImage(view.image);
      if (seq !== renderSequence || state.view !== viewName) return;
      clearCanvas(ctx);
      clearCanvas(liveCtx);
      ctx.drawImage(img, 0, 0, 1100, 1100);
      liveCtx.drawImage(img, 0, 0, 1100, 1100);
      els.liveCanvas.classList.add('is-ready');
    } catch (err) {
      console.error(err);
      toast('Görünüm yüklenemedi.');
    } finally {
      if (seq === renderSequence) els.loader.classList.remove('is-visible');
    }
  }

  function updateViewControls() {
    document.querySelectorAll('[data-tolun-customizer] .view-btn,[data-tolun-customizer] .view-thumb').forEach(btn => {
      btn.classList.toggle('is-active', btn.dataset.view === state.view);
    });
  }

  function setView(viewName) {
    if (!['front', 'side', 'back'].includes(viewName)) return;
    state.view = viewName;
    updateViewControls();
    if (viewName === 'front') renderFront(false);
    else renderStaticView(viewName);
  }

  function renderChips() {
    if (!els.chips) return;
    els.chips.innerHTML = TYPES.map(type => {
      const item = selectedItem(type);
      if (!item) return '';
      return `<span class="selection-chip"><span>${escapeHtml(typeLabel(type))}</span>${escapeHtml(displayPartName(item.name))}</span>`;
    }).join('');
  }

  function typeLabel(type) {
    return ({ dial:'Kadran', case:'Kasa', strap:'Kordon', hands:'Kol' })[type] || type;
  }

  function initializeDialCollection() {
    const collections = dialCollections();
    if (!collections.length) {
      activeDialCollection = '';
      return;
    }

    const requestedExists = requestedCollection &&
      collections.some(item => item.handle === requestedCollection);

    const selectedDial = selectedItem('dial');
    const selectedHandle = normalizeCollectionHandle(selectedDial?.collection || '');
    const selectedExists = collections.some(item => item.handle === selectedHandle);

    activeDialCollection = requestedExists
      ? requestedCollection
      : selectedExists
        ? selectedHandle
        : collections[0].handle;

    const activeItems = visibleItems('dial');
    const selectedIsVisible = activeItems.some(item => item.id === state.selected.dial);

    if (!selectedIsVisible) {
      const firstAvailable = activeItems.find(isAvailable) || activeItems[0];
      if (firstAvailable) state.selected.dial = firstAvailable.id;
    }
  }

  function updateCollectionQuery(handle) {
    try {
      const url = new URL(window.location.href);
      if (handle) url.searchParams.set('collection', handle);
      else url.searchParams.delete('collection');
      window.history.replaceState({}, '', url);
    } catch (_) {}
  }

  function renderDialCollectionTabs() {
    const holder = els.dialCollectionTabs;
    if (!holder) return;

    const collections = dialCollections();
    holder.innerHTML = '';
    holder.classList.toggle('is-compact-fit', collections.length > 1 && collections.length <= 3);
    holder.classList.toggle('is-scrollable-tabs', collections.length > 3);
    holder.style.setProperty('--tolun-tab-count', String(Math.max(1, collections.length)));

    if (collections.length <= 1) {
      holder.closest('.dial-collection-tabs-shell')?.classList.add('is-hidden');
      return;
    }

    holder.closest('.dial-collection-tabs-shell')?.classList.remove('is-hidden');

    collections.forEach(collection => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'dial-collection-tab';
      button.dataset.collection = collection.handle;
      button.setAttribute('role', 'tab');
      button.setAttribute(
        'aria-selected',
        collection.handle === activeDialCollection ? 'true' : 'false'
      );
      button.textContent = collection.title;

      if (collection.handle === activeDialCollection) {
        button.classList.add('is-active');
      }

      button.addEventListener('click', () => {
        if (activeDialCollection === collection.handle) return;

        activeDialCollection = collection.handle;

        const nextItems = visibleItems('dial');
        const currentStillVisible = nextItems.some(
          item => item.id === state.selected.dial && isAvailable(item)
        );

        if (!currentStillVisible) {
          const nextDial = nextItems.find(isAvailable) || nextItems[0];
          if (nextDial) state.selected.dial = nextDial.id;
        }

        renderDialCollectionTabs();
        renderOptions('dial');
        updateSelectedOption('dial');
        renderChips();
        renderPrice();

        state.view = 'front';
        updateViewControls();
        renderFront(false);
        updateCollectionQuery(activeDialCollection);

        requestAnimationFrame(() => {
          els.dialCollectionTabs
            ?.querySelector('.dial-collection-tab.is-active')
            ?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        });
      });

      holder.appendChild(button);
    });
  }

  function renderOptions(type) {
    const holder = document.getElementById(`${type}Options`);
    if (!holder) return;
    const frag = document.createDocumentFragment();

    visibleItems(type).forEach(item => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'option-card';
      button.dataset.id = item.id;
      button.dataset.type = type;
      button.title = displayPartName(item.name);
      button.setAttribute('aria-pressed', state.selected[type] === item.id ? 'true' : 'false');
      if (state.selected[type] === item.id) button.classList.add('is-selected');
      if (!isAvailable(item)) {
        button.classList.add('is-unavailable');
        button.disabled = true;
      }

      const stockMeta = item.stock === null || item.stock === undefined
        ? ''
        : `<span class="option-meta">Stok: ${Number(item.stock)}</span>`;

      button.innerHTML = `
        <span class="option-image"><img src="${escapeAttr(item.thumbnail)}" alt="${escapeAttr(displayPartName(item.name))}" loading="lazy" decoding="async"></span>
        <span class="option-name">${escapeHtml(displayPartName(item.name))}</span>
        ${stockMeta}`;

      button.addEventListener('pointerenter', () => loadImage(item.image).catch(() => {}), { once: true });
      button.addEventListener('touchstart', () => loadImage(item.image).catch(() => {}), { once: true, passive: true });
      frag.appendChild(button);
    });

    if (!frag.childNodes.length) {
      const message = document.createElement('p');
      message.className = 'tolun-empty-component-message';
      message.setAttribute('role', 'status');
      message.style.cssText = 'grid-column:1/-1;padding:18px 0;font-size:12px;line-height:1.6;color:#746f68';
      message.textContent = 'Bu kategoride şu anda seçilebilir parça bulunmuyor. Lütfen daha sonra tekrar deneyin.';
      frag.appendChild(message);
    }
    holder.replaceChildren(frag);

    if (holder.dataset.optionsBound !== 'true') {
      holder.dataset.optionsBound = 'true';
      holder.addEventListener('click', e => {
        const button = e.target.closest('.option-card');
        if (!button || button.disabled) return;
        selectComponent(type, button.dataset.id, button);
      });
    }
  }

  function updateSelectedOption(type, activeButton = null) {
    const holder = document.getElementById(`${type}Options`);
    if (!holder) return;
    holder.querySelectorAll('.option-card.is-selected').forEach(btn => {
      btn.classList.remove('is-selected');
      btn.setAttribute('aria-pressed', 'false');
    });
    const button = activeButton || holder.querySelector(`.option-card[data-id="${cssEscape(state.selected[type])}"]`);
    if (button) {
      button.classList.add('is-selected');
      button.setAttribute('aria-pressed', 'true');
    }
  }

  function selectComponent(type, id, button) {
    const item = byId(type, id);
    if (!isAvailable(item) || state.selected[type] === id) return;

    state.selected[type] = id;
    updateSelectedOption(type, button);
    renderChips();
    renderPrice();

    // Bir parça seçildiğinde otomatik olarak ön görünüme dön.
    state.view = 'front';
    updateViewControls();
    renderFront(false);
  }

  function selectionPayload() {
    const selections = {};
    TYPES.forEach(type => {
      const item = selectedItem(type);
      selections[type] = item ? {
        id: item.id,
        name: displayPartName(item.name),
        sku: item.sku,
        shopifyVariantId: item.shopifyVariantId,
        price: item.price,
        collection: type === 'dial'
          ? normalizeCollectionHandle(item.collection || '')
          : null,
        stock: item.stock,
        available: isAvailable(item)
      } : null;
    });

    return {
      productId: product.id,
      productName: product.name,
      quantity: state.quantity,
      engraving: ENGRAVING_ENABLED ? state.engraving : '',
      total: total(),
      selections
    };
  }

  // Dört gerçek Shopify varyantını tek Ajax isteğinde sepete ekle.
  // Aynı tasarımın parçalarını özel grup anahtarıyla ilişkilendir.
  // Ana ürün mağazada ayarlanmışsa fiyat onun üzerinden, parça stokları ayrı satırlardan yürür.
  const preorderModeEnabled = () => els.root?.dataset.tolunPreorder === 'true';
  let cartSubmissionInProgress = false;
  async function addToShopifyCart() {
    if (cartSubmissionInProgress) return;
    cartSubmissionInProgress = true;
    els.addToCart.disabled = true;
    els.addToCart.setAttribute('aria-busy', 'true');
    const originalLabel = els.addToCart.querySelector('span');
    const previousLabel = originalLabel?.textContent;
    if (originalLabel) originalLabel.textContent = 'Stok kontrol ediliyor…';
    try {
      await performShopifyCartAdd();
    } finally {
      cartSubmissionInProgress = false;
      els.addToCart.disabled = false;
      els.addToCart.removeAttribute('aria-busy');
      if (originalLabel && previousLabel) originalLabel.textContent = previousLabel;
    }
  }

  async function performShopifyCartAdd() {
    const previousWarning=document.getElementById('tolunCartError');
    if(previousWarning)previousWarning.hidden=true;
    const invalid=TYPES.find(type=>!selectedItem(type)||!Number.isSafeInteger(Number(selectedItem(type)?.shopifyVariantId))||Number(selectedItem(type)?.shopifyVariantId)<=0);
    if(invalid){
      showCartFailure('Bir parçanın Shopify bilgisi henüz doğrulanamadı. Sayfayı yenileyip tekrar deneyin.');
      return;
    }
    const qty = Number(state.quantity);
    if (!Number.isInteger(qty) || qty < 1 || qty > 10) {
      showCartFailure('Lütfen 1 ile 10 arasında geçerli bir adet seçin.');
      return;
    }

    // Yalnızca sayfa ilk açıldığında alınan stok yeterli değil: yönetici stokları
    // başka sekmede değiştirmiş veya müşteri daha önce sepete ürün eklemiş olabilir.
    // Her Sepete Ekle tıklamasında Shopify'dan dört ürünün GÜNCEL verisini çek.
    try {
      const freshResults = await Promise.all(TYPES.map(async type => {
        const variants = await fetchComponentProduct(COMPONENT_PRODUCT_HANDLES[type], true);
        const selected = selectedItem(type);
        const fresh = variants.find(v => Number(v.id) === Number(selected.shopifyVariantId));
        if (!fresh) throw new Error(`${displayPartName(selected.name)} için güncel ürün bilgisi alınamadı. Lütfen sayfayı yenileyin.`);
        const stock = Number(fresh.stock);
        if (!Number.isFinite(stock)) throw new Error(`${displayPartName(selected.name)} için güncel stok bilgisi doğrulanamadı. Lütfen tekrar deneyin.`);
        selected.stock = stock;
        selected.available = fresh.available !== false;
        return { type, selected, stock, available: selected.available };
      }));
      const root = window.Shopify?.routes?.root || '/';
      const path = `${root.endsWith('/') ? root : `${root}/`}cart.js`;
      const response = await fetch(path, {
        credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error('Sepetteki güncel adetler okunamadı. Lütfen tekrar deneyin.');
      const currentCart = await response.json();
      const labels = { dial: 'kadran', case: 'kasa', strap: 'kordon', hands: 'kol' };
      const insufficient = freshResults.find(({selected,stock,available}) => {
        const already = (currentCart.items || [])
          .filter(line => Number(line.variant_id) === Number(selected.shopifyVariantId))
          .reduce((sum, line) => sum + Number(line.quantity || 0), 0);
        return !available || already + qty > stock;
      });
      if (insufficient) {
        const {type,selected,stock} = insufficient;
        const inCart = (currentCart.items || [])
          .filter(line => Number(line.variant_id) === Number(selected.shopifyVariantId))
          .reduce((sum,line) => sum + Number(line.quantity || 0), 0);
        const remaining = Math.max(0, stock - inCart);
        showCartFailure(`STOK YETERSİZ: ${displayPartName(selected.name)} (${labels[type]}) için ${remaining} adet daha alabilirsiniz. Şu an ${qty} adet eklemeye çalışıyorsunuz.${inCart ? ` Bu parçadan sepetinizde zaten ${inCart} adet var.` : ''} Lütfen miktarı azaltın veya farklı bir parça seçin.`);
        renderOptions(type);
        return;
      }
    } catch(err) {
      showCartFailure(err.message || 'Güncel stok bilgileri doğrulanamadı. Lütfen tekrar deneyin.');
      return;
    }

    // Ana ürün ayarı kaldırılmışsa dört adet 0 TL parça göndermeye izin verme.
    if (!Number.isSafeInteger(Number(product.parentVariantId)) || Number(product.parentVariantId) <= 0) {
      showCartFailure('Saatin satış ürünü ayarlanmamış. Lütfen mağaza yetkilisine haber verin.');
      return;
    }

    const groupId = `TOLUN-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    const groupProperties = { _TolunGrup: groupId, _TolunTur: 'ozellestirilmis-saat' };
    const labelByType = { dial: 'Kadran', case: 'Kasa', strap: 'Kordon', hands: 'Kol' };
    const lines = TYPES.map(type => {
      const item = selectedItem(type);
      return {
        id: Number(item.shopifyVariantId),
        quantity: qty,
        properties: {
          ...groupProperties,
          'Saat': 'TOLUN Kişiselleştirilmiş Saat',
          'Parça': labelByType[type],
          'Seçim': displayPartName(item.name || item.sku || ''),
          ...(type === 'dial' ? { 'Koleksiyon': collectionTitle(normalizeCollectionHandle(item.collection || '')) } : {}),
          ...(ENGRAVING_ENABLED && type === 'dial' && state.engraving.trim() ? { 'Gravür': state.engraving.trim() } : {})
        }
      };
    });

    const parentId = Number(product.parentVariantId);
    if (Number.isSafeInteger(parentId) && parentId > 0) {
      // Ana ürünün stok takibi açıksa bu beşinci satırın stoğu da ayrı düşebilir.
      lines.unshift({
        id: parentId,
        quantity: qty,
        properties: {
          ...groupProperties,
          'Saat': 'TOLUN Kişiselleştirilmiş Saat',
          'Parça': 'Ana saat',
          'Kadran': displayPartName(selectedItem('dial').name),
          'Kasa': selectedItem('case').name,
          'Kordon': selectedItem('strap').name,
          'Kol': displayPartName(selectedItem('hands').name),
          ...(ENGRAVING_ENABLED && state.engraving.trim() ? { 'Gravür': state.engraving.trim() } : {})
        }
      });
    }

    const loadingLabel = els.addToCart.querySelector('span');
    if (loadingLabel) loadingLabel.textContent = preorderModeEnabled() ? 'Ön sipariş hazırlanıyor…' : 'Sepete ekleniyor…';
    try {
      const root = window.Shopify?.routes?.root || '/';
      const path = `${root.endsWith('/') ? root : `${root}/`}cart/add.js`;
      const response = await fetch(path, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: lines })
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || error.message || `Sepet hatası (${response.status})`);
      }
      // Bazı Shopify yanıtlarında istek kabul edilse bile eksik satır kalabilir.
      // Yeni oluşturduğumuz benzersiz grup anahtarıyla tüm 5 satırı doğrula.
      const cartRes=await fetch(`${root.endsWith('/') ? root : `${root}/`}cart.js`,{credentials:'same-origin',cache:'no-store',headers:{Accept:'application/json'}});
      if (!cartRes.ok) throw new Error('Sepet ekleme sonrası doğrulanamadı. Lütfen sepetinizi kontrol edin.');
      const liveCart=await cartRes.json();
      const grouped=(liveCart.items||[]).filter(line=>line.properties?._TolunGrup===groupId);
      const expectedIds=new Set(lines.map(line=>Number(line.id)));
      const complete=grouped.length===5 &&
        grouped.every(line=>expectedIds.has(Number(line.variant_id)) && Number(line.quantity)===qty) &&
        expectedIds.size===5;
      if (!complete) {
        await cleanupIncompleteGroup(groupId, root);
        window.dispatchEvent(new CustomEvent('tolun:cart-updated'));
        throw new Error('Saatin tüm parçaları sepete eksiksiz eklenemedi. Eksik tasarım temizlenmeye çalışıldı. Sepetinizi kontrol edip yeniden deneyin.');
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(selectionPayload()));
      window.dispatchEvent(new CustomEvent('tolun:cart-updated',{detail:{cart:liveCart}}));
      if (window.TolunCartDrawer) window.TolunCartDrawer.open();
      else window.location.assign(window.TolunTheme?.cartUrl || `${root}cart`);
    } catch (error) {
      console.error('[TOLUN] Sepete ekleme hatası:', error);
      // Ajax toplu eklemede Shopify hata verdiğinde kısmi ekleme olasılığı vardır.
      // Otomatik tekrar gönderme yapmıyoruz; önce sepeti kontrol etmek gerekir.
      const text=String(error.message||'Beklenmeyen hata');
      const inventoryIssue=/422|stock|stok|sold out|inventory|not available|unavailable|maximum|quantity|miktar|tüken/i.test(text);
      let message=inventoryIssue
        ? `STOK YETERSİZ OLABİLİR: Seçtiğiniz bir parça istenen miktarda eklenemedi. ${text} Sepetinizi kontrol edip miktarı azaltın veya farklı parça seçin.`
        : `Saat sepete eklenemedi: ${text} Tekrar denemeden önce sepetinizi kontrol edin.`;
      // Bir toplu isteğin kısmen işlenmiş olması ihtimaline karşı yalnızca
      // bu denemeye ait benzersiz grup anahtarıyla kalan satırları temizle.
      try {
        await cleanupIncompleteGroup(groupId, window.Shopify?.routes?.root || '/');
        window.dispatchEvent(new CustomEvent('tolun:cart-updated'));
      } catch (cleanupError) {
        console.error('[TOLUN] Eksik grup temizlenemedi:', cleanupError);
        message += ' Otomatik sepet temizliği doğrulanamadı; lütfen sepetinizi mutlaka kontrol edin.';
      }
      showCartFailure(message);
    }
  }

  // Yalnızca bu ekleme denemesine ait satırları kaldırır; eski saatlere dokunmaz.
  async function cleanupIncompleteGroup(groupId, root) {
    const prefix = root.endsWith('/') ? root : `${root}/`;
    for (let attempt = 0; attempt < 7; attempt++) {
      const cartResponse = await fetch(`${prefix}cart.js`, {
        credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' }
      });
      if (!cartResponse.ok) throw new Error('Eksik tasarımın sepetten temizlenmesi doğrulanamadı. Sepetinizi kontrol edin.');
      const cart = await cartResponse.json();
      const line = (cart.items || []).find(item => item.properties?._TolunGrup === groupId);
      if (!line) return;
      const changeResponse = await fetch(`${prefix}cart/change.js`, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: line.key, quantity: 0 })
      });
      if (!changeResponse.ok) throw new Error('Eksik tasarım tam temizlenemedi. Sepetinizi kontrol edin.');
    }
    throw new Error('Eksik tasarım tamamen temizlenemedi. Sepetinizi kontrol edin.');
  }

  function showCartFailure(message) {
    toast(message);
    let warning=document.getElementById('tolunCartError');
    if (!warning) {
      warning=document.createElement('div');
      warning.id='tolunCartError';
      warning.setAttribute('role','alert');
      els.addToCart.insertAdjacentElement('afterend',warning);
    }
    warning.classList.add('tolun-cart-stock-alert');
    warning.textContent=message;
    warning.hidden=false;
    warning.scrollIntoView({behavior:'smooth',block:'nearest'});
  }

  function saveDesign() {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(selectionPayload()));
    toast('Tasarım bu tarayıcıya kaydedildi.');
  }

  function restoreDesign() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);

      TYPES.forEach(type => {
        const savedSelection = saved?.selections?.[type];
        if (!savedSelection) return;

        const items = components[type] || [];
        const savedVariantId = Number(savedSelection.shopifyVariantId);
        const savedSku = String(savedSelection.sku || '').trim().toUpperCase();
        const savedId = savedSelection.id;

        let item = Number.isSafeInteger(savedVariantId) && savedVariantId > 0
          ? items.find(candidate => Number(candidate.shopifyVariantId) === savedVariantId)
          : null;

        if (!item && savedSku) {
          item = items.find(candidate =>
            String(candidate.sku || '').trim().toUpperCase() === savedSku
          );
        }

        if (!item && savedId) {
          item = items.find(candidate => candidate.id === savedId);
        }

        if (isAvailable(item)) state.selected[type] = item.id;
      });

      state.quantity = Math.max(1, Math.min(10, Number(saved.quantity || 1)));
      state.engraving = ENGRAVING_ENABLED
        ? String(saved.engraving || '').slice(0, Number(product.maxEngravingLength || 30))
        : '';
    } catch (_) {}
  }

  function toast(message) {
    els.toast.textContent = message;
    els.toast.classList.add('is-visible');
    clearTimeout(toast.timer);
    toast.timer = setTimeout(() => els.toast.classList.remove('is-visible'), 2200);
  }

  function escapeHtml(value = '') {
    return String(value).replace(/[&<>'"]/g, c => ({
      '&':'&amp;', '<':'&lt;', '>':'&gt;', "'":'&#39;', '"':'&quot;'
    })[c]);
  }
  function escapeAttr(value = '') { return escapeHtml(value); }
  function cssEscape(value = '') {
    if (window.CSS?.escape) return CSS.escape(String(value));
    return String(value).replace(/(["'\\.#:[\]()])/g, '\\$1');
  }

  function updateMobileLivePreview() {
    if (!els.livePreview || !els.previewPanel || !els.root) return;

    const mobile = window.matchMedia('(max-width: 920px)').matches;
    if (!mobile) {
      els.livePreview.classList.remove('is-visible');
      return;
    }

    const previewRect = els.previewPanel.getBoundingClientRect();
    const watchRect = els.watchStage?.getBoundingClientRect();
    const optionsRect = els.optionsPanel?.getBoundingClientRect();
    const rootRect = els.root.getBoundingClientRect();

    // Büyük saat görselinin kendisi ekrandan çıkar çıkmaz preview açılsın.
    // Böylece görünüm butonları / seçim özetleri hâlâ ekrandayken bile
    // kullanıcı saatini sağ üstte görmeye devam eder.
    const topSafeArea = 92;
    const watchGone = watchRect
      ? watchRect.bottom < topSafeArea
      : previewRect.bottom < topSafeArea;

    // Mobil düzen değişikliklerinde options paneli yukarı geldiyse de
    // preview'i erkenden aç.
    const optionsStarted = optionsRect
      ? optionsRect.top < Math.min(window.innerHeight * 0.72, 520)
      : false;

    const customizerStillOnScreen =
      rootRect.bottom > 180 && rootRect.top < window.innerHeight;

    els.livePreview.classList.toggle(
      'is-visible',
      (watchGone || optionsStarted) && customizerStillOnScreen
    );
  }

  function bindMobileLivePreview() {
    let ticking = false;
    const requestUpdate = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        updateMobileLivePreview();
        ticking = false;
      });
    };

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate, { passive: true });
    window.addEventListener('orientationchange', requestUpdate, { passive: true });
    requestUpdate();
  }

  function applyEngravingMode() {
    const engravingSection = els.engravingInput?.closest('.option-section');

    if (!ENGRAVING_ENABLED) {
      state.engraving = '';

      if (engravingSection) {
        engravingSection.hidden = true;
        engravingSection.setAttribute('aria-hidden', 'true');
      }

      if (els.engravingInput) {
        els.engravingInput.value = '';
        els.engravingInput.disabled = true;
      }

      if (els.engravingCount) {
        els.engravingCount.textContent = `0 / ${product.maxEngravingLength || 30}`;
      }

      const stepCount = els.root?.querySelector('.step-count');
      if (stepCount) stepCount.textContent = '04 ADIM';

      return;
    }

    if (engravingSection) {
      engravingSection.hidden = false;
      engravingSection.removeAttribute('aria-hidden');
    }

    if (els.engravingInput) els.engravingInput.disabled = false;
  }

  function bindUI() {
    document.querySelectorAll('[data-tolun-customizer] .section-head').forEach(head => {
      head.addEventListener('click', () => {
        const section = head.closest('.option-section');
        const willOpen = !section.classList.contains('is-open');

        document.querySelectorAll('[data-tolun-customizer] .option-section').forEach(s => {
          s.classList.remove('is-open');
          s.querySelector('.section-head')?.setAttribute('aria-expanded', 'false');
          const chev = s.querySelector('.chevron');
          if (chev) chev.textContent = '⌄';
        });

        if (willOpen) {
          section.classList.add('is-open');
          head.setAttribute('aria-expanded', 'true');
          const chev = section.querySelector('.chevron');
          if (chev) chev.textContent = '⌃';
        }
      });
    });

    document.querySelectorAll('[data-tolun-customizer] .view-btn,[data-tolun-customizer] .view-thumb').forEach(btn => {
      btn.addEventListener('click', () => setView(btn.dataset.view));
    });

    if (ENGRAVING_ENABLED && els.engravingInput) {
      els.engravingInput.addEventListener('input', e => {
        state.engraving = e.target.value;
        if (els.engravingCount) {
          els.engravingCount.textContent = `${state.engraving.length} / ${product.maxEngravingLength || 30}`;
        }
        renderPrice();
      });
    }

    document.getElementById('qtyMinus').addEventListener('click', () => {
      state.quantity = Math.max(1, state.quantity - 1);
      els.qtyValue.textContent = state.quantity;
      renderPrice();
    });

    document.getElementById('qtyPlus').addEventListener('click', () => {
      state.quantity = Math.min(10, state.quantity + 1);
      els.qtyValue.textContent = state.quantity;
      renderPrice();
    });

    document.getElementById('saveDesignBtn').addEventListener('click', saveDesign);
    els.addToCart.addEventListener('click', addToShopifyCart);
  }

  function preloadEverything() {
    const sources = [];
    TYPES.forEach(type => (components[type] || []).forEach(item => sources.push(item.image)));
    ['side', 'back'].forEach(view => {
      const src = data.views?.[view]?.image;
      if (src) sources.push(src);
    });

    const unique = [...new Set(sources)];
    let index = 0;
    const batchSize = 6;

    const pump = () => {
      const batch = unique.slice(index, index + batchSize);
      index += batchSize;
      batch.forEach(src => loadImage(src).catch(() => {}));
      if (index < unique.length) {
        if ('requestIdleCallback' in window) requestIdleCallback(pump, { timeout: 500 });
        else setTimeout(pump, 60);
      }
    };

    if ('requestIdleCallback' in window) requestIdleCallback(pump, { timeout: 350 });
    else setTimeout(pump, 100);
  }


  /* =========================================================
     TOLUN AUTO ASSET REFRESH
     Güncel asset URL değiştiğinde açık eski sekmeyi otomatik yeniler.
     ========================================================= */
  let tolunVersionCheckInProgress = false;
  let tolunLastVersionCheckAt = 0;

  function normalizeAssetUrl(value) {
    try {
      const url = new URL(value, window.location.href);
      url.hash = '';
      return url.href;
    } catch (_) {
      return String(value || '');
    }
  }

  async function checkForCustomizerUpdate() {
    if (tolunVersionCheckInProgress) return;

    const now = Date.now();
    if (now - tolunLastVersionCheckAt < 15000) return;

    tolunVersionCheckInProgress = true;
    tolunLastVersionCheckAt = now;

    try {
      const pageUrl = new URL(window.location.href);
      pageUrl.searchParams.set('_tolun_asset_check', String(now));

      const response = await fetch(pageUrl.href, {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          'Accept': 'text/html',
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });

      if (!response.ok) return;

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const script = [...doc.querySelectorAll('script[src]')]
        .find(node => /\/tolun-customizer\.js(?:\?|$)/i.test(node.getAttribute('src') || ''));

      const latestSrc = normalizeAssetUrl(script?.src || '');
      const currentSrc = normalizeAssetUrl(CURRENT_CUSTOMIZER_SCRIPT_SRC);

      if (latestSrc && currentSrc && latestSrc !== currentSrc) {
        window.location.reload();
      }
    } catch (error) {
      console.warn('[TOLUN] Customizer sürüm kontrolü yapılamadı.', error);
    } finally {
      tolunVersionCheckInProgress = false;
    }
  }

  function bindAutomaticCustomizerRefresh() {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') checkForCustomizerUpdate();
    });

    window.addEventListener('pageshow', event => {
      if (event.persisted) checkForCustomizerUpdate();
    });

    window.addEventListener('focus', checkForCustomizerUpdate);

    setInterval(() => {
      if (document.visibilityState === 'visible') checkForCustomizerUpdate();
    }, 5 * 60 * 1000);
  }

  async function init() {
    const liveComponents = await loadShopifyComponents();
    hydrateComponentsFromShopify(liveComponents);

    restoreDesign();
    applyEngravingMode();
    // Varsayılan / kayıtlı varyant silindiyse mevcut ilk stoklu parçaya geç.
    TYPES.forEach(type => {
      if (!isAvailable(selectedItem(type))) {
        const replacement = (components[type] || []).find(isAvailable)
          || (components[type] || [])[0];
        state.selected[type] = replacement?.id || null;
      }
    });
    initializeDialCollection();
    renderDialCollectionTabs();
    TYPES.forEach(renderOptions);
    bindUI();
    bindMobileLivePreview();
    bindAutomaticCustomizerRefresh();
    renderChips();

    if (els.engravingInput) els.engravingInput.value = state.engraving;
    if (els.engravingCount) {
      els.engravingCount.textContent = `${state.engraving.length} / ${product.maxEngravingLength || 30}`;
    }
    els.qtyValue.textContent = state.quantity;
    renderPrice();
    updateViewControls();

    await renderFront(true);
    preloadEverything();
  }

  init();
})();
